/**
 * AdminNotificationBell
 * ─────────────────────
 * Bell icon + pink numeric badge for the Navbar (admin only).
 *
 * Match the visual language Saleem requested: circular pill with the count,
 * pinned to the top-right of the icon (same style used by the saved-colleges
 * Heart in Navbar.jsx today).
 *
 * Behaviour:
 *   - Click opens a dropdown with the most-recent events across every source.
 *   - Each item has an "Open" link that routes to /admin?tab=<slug> and
 *     marks that table as seen (unread count drops immediately).
 *   - "Mark all seen" clears every counter.
 *   - Optional subtle audible ping on brand-new events (respects user's
 *     browser autoplay policy — silently degrades if blocked).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronRight, CheckCheck, RefreshCw, Loader2 } from 'lucide-react';
import { useAdminNotifications, NOTIF_SOURCES } from '../../hooks/useAdminNotifications';

const ACCENT_CLASSES = {
    orange:  'bg-orange-500/15  text-orange-300  border-orange-500/30',
    sky:     'bg-sky-500/15     text-sky-300     border-sky-500/30',
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    rose:    'bg-rose-500/15    text-rose-300    border-rose-500/30',
    amber:   'bg-amber-500/15   text-amber-300   border-amber-500/30',
    indigo:  'bg-indigo-500/15  text-indigo-300  border-indigo-500/30',
    purple:  'bg-purple-500/15  text-purple-300  border-purple-500/30',
    cyan:    'bg-cyan-500/15    text-cyan-300    border-cyan-500/30',
};

// Module-level audio guard so a single "new lead" event doesn't trigger a
// double ping from the desktop + mobile bell instances that both mount.
let _lastPingedTotal = null;
function playBellPing() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = 880;
        g.gain.value = 0.06;
        o.connect(g);
        g.connect(ctx.destination);
        o.start();
        setTimeout(() => { o.stop(); ctx.close(); }, 140);
    } catch { /* autoplay policy — silent degrade */ }
}

function timeAgo(iso) {
    if (!iso) return '';
    const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60)    return `${secs}s ago`;
    if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
}

export default function AdminNotificationBell({ enabled = true }) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);

    const { totalUnread, unreadByTable, recent, loading, lastFetchedAt, markSeen, markAllSeen, refresh } =
        useAdminNotifications({ enabled });

    // Audible ping when the unread count goes UP. Module-level guard so a
    // single new-lead event doesn't ping twice (desktop + mobile mounts).
    useEffect(() => {
        if (!enabled) return;
        if (_lastPingedTotal !== null && totalUnread > _lastPingedTotal) {
            playBellPing();
        }
        _lastPingedTotal = totalUnread;
    }, [totalUnread, enabled]);

    // Close on outside click / Esc
    useEffect(() => {
        if (!open) return undefined;
        function onDown(e) {
            if (dropdownRef.current?.contains(e.target)) return;
            if (triggerRef.current?.contains(e.target)) return;
            setOpen(false);
        }
        function onKey(e) { if (e.key === 'Escape') setOpen(false); }
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // NOTE: document.title updating is NOT done here — it lives in the
    // singleton store inside useAdminNotifications.js. Reason: two bell
    // instances render simultaneously (desktop + mobile navbars) and each
    // one running its own title-effect produced a "(N) (N) Eager" double
    // prefix in earlier iteration_27 tests.

    if (!enabled) return null;

    // Ceiling display: 99+
    const badgeText = totalUnread > 99 ? '99+' : String(totalUnread);

    return (
        <div className="relative">
            <button
                type="button"
                ref={triggerRef}
                onClick={() => setOpen(o => !o)}
                data-testid="admin-notification-bell"
                aria-label={totalUnread ? `${totalUnread} new admin notifications` : 'Admin notifications'}
                className="relative p-2 rounded-full text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                title={totalUnread ? `${totalUnread} unread` : 'No new activity'}
            >
                <Bell className={`w-5 h-5 ${totalUnread > 0 ? 'text-pink-400' : ''}`} />
                {totalUnread > 0 && (
                    <span
                        data-testid="admin-notification-count"
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-pink-500 text-white text-[10px] font-black flex items-center justify-center rounded-full ring-2 ring-slate-900 animate-bounce-in"
                    >
                        {badgeText}
                    </span>
                )}
            </button>

            {open && (
                <div
                    ref={dropdownRef}
                    data-testid="admin-notification-dropdown"
                    className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-24px)] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden z-50"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                        <div>
                            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Inbox</div>
                            <div className="text-sm text-white font-bold">
                                {totalUnread === 0 ? 'All caught up 🎯' : `${totalUnread} new`}
                            </div>
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={refresh}
                                title="Refresh"
                                data-testid="admin-notification-refresh"
                                className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
                            >
                                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            </button>
                            {totalUnread > 0 && (
                                <button
                                    onClick={markAllSeen}
                                    title="Mark all as seen"
                                    data-testid="admin-notification-mark-all"
                                    className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
                                >
                                    <CheckCheck className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Per-source unread strip */}
                    <div className="grid grid-cols-4 gap-1 px-3 py-3 border-b border-slate-800 bg-slate-950/40">
                        {NOTIF_SOURCES.map(src => {
                            const c = unreadByTable[src.table] || 0;
                            return (
                                <Link
                                    key={src.table}
                                    to={`/admin?tab=${src.tab}`}
                                    onClick={() => { markSeen(src.table); setOpen(false); }}
                                    data-testid={`admin-notification-source-${src.table}`}
                                    className={`text-[10px] font-bold uppercase tracking-wide rounded-lg px-2 py-1.5 border text-center flex flex-col items-center gap-0.5 ${
                                        c > 0
                                            ? ACCENT_CLASSES[src.accent] || ACCENT_CLASSES.rose
                                            : 'bg-slate-900/60 border-slate-800 text-slate-500 hover:text-slate-300'
                                    }`}
                                >
                                    <span className="truncate max-w-full">{src.label}</span>
                                    <span className={`font-black ${c > 0 ? 'text-base leading-none' : 'text-xs leading-none opacity-60'}`}>
                                        {c > 99 ? '99+' : c}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Recent list */}
                    <div className="max-h-[360px] overflow-y-auto">
                        {recent.length === 0 ? (
                            <div className="py-10 text-center text-slate-500 text-sm">
                                <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                                Nothing new since last check.
                                {lastFetchedAt && (
                                    <div className="text-[10px] uppercase tracking-widest text-slate-600 mt-1">
                                        Last polled {timeAgo(new Date(lastFetchedAt).toISOString())}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <ul className="divide-y divide-slate-800/80">
                                {recent.map(ev => {
                                    const accent = ACCENT_CLASSES[ev.accent] || ACCENT_CLASSES.rose;
                                    return (
                                        <li key={ev.id}>
                                            <Link
                                                to={`/admin?tab=${ev.tab}`}
                                                onClick={() => { markSeen(ev.table); setOpen(false); }}
                                                data-testid={`admin-notification-item-${ev.table}`}
                                                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-800/60 transition-colors group"
                                            >
                                                <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-md border text-[9px] font-black uppercase tracking-widest ${accent}`}>
                                                    {ev.label}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-bold text-white truncate">{ev.title}</div>
                                                    <div className="text-[11px] text-slate-400 truncate">{ev.sub}</div>
                                                </div>
                                                <div className="text-[10px] text-slate-500 shrink-0">{timeAgo(ev.created_at)}</div>
                                                <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="px-4 py-2 border-t border-slate-800 bg-slate-950/40 flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-widest text-slate-600">
                            Polls every 20s
                        </div>
                        <Link
                            to="/admin"
                            onClick={() => setOpen(false)}
                            data-testid="admin-notification-open-dashboard"
                            className="text-[11px] font-black uppercase tracking-widest text-indigo-400 hover:text-indigo-300"
                        >
                            Open Admin →
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}
